import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import { useLiveCallsCount } from "@/hooks/useLiveCallsCount";
import { can, firstAllowedPath, isPlatformOperator } from "@/lib/permissions";
import { PageHeader, StatCard } from "@/components/common";
import {
  UsersThree, Storefront, Megaphone, Wallet, ShareNetwork, WaveSine, StackSimple,
  UserCircle, Broadcast, ChartLineUp,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const PALETTE = ["#8b5cf6", "#10b981", "#f59e0b", "#0ea5e9", "#f43f5e", "#64748b", "#ec4899"];

const fmtDay = (d) => {
  const dt = new Date(`${d}T00:00:00`);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const prettyLabel = (s) =>
  String(s || "unknown").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function LiveCallsTile() {
  const count = useLiveCallsCount();
  return (
    <Link to="/live-calls" className="block focus:outline-none focus:ring-2 focus:ring-primary rounded-xl" data-testid="dashboard-live-calls-tile">
      <StatCard testid="stat-live-calls" icon={Broadcast} label="Live Calls"
        value={count} hint={count > 0 ? "Connected right now — click to view" : "No calls connected"}
        tone={count > 0 ? "success" : "muted"} />
    </Link>
  );
}

function ChartCard({ title, subtitle, testid, children, empty }) {
  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {empty ? (
        <div className="h-[260px] grid place-items-center text-center" data-testid={`${testid}-empty`}>
          <div className="text-muted-foreground">
            <ChartLineUp className="w-8 h-8 mx-auto mb-2 opacity-50" weight="duotone" />
            <p className="text-sm">No data yet</p>
          </div>
        </div>
      ) : (
        <div className="h-[260px]" style={{ fontSize: 12 }}>{children}</div>
      )}
    </div>
  );
}

const tooltipStyle = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 10,
  fontSize: 12,
  color: "hsl(var(--popover-foreground))",
};
const axis = { stroke: "hsl(var(--muted-foreground))", fontSize: 11 };

function SignupsChart({ data, showResellers }) {
  const empty = !data || data.every((d) => (d.customers || 0) + (d.resellers || 0) === 0);
  return (
    <ChartCard testid="chart-signups" title={showResellers ? "New signups" : "My new customer signups"}
      subtitle="Last 30 days" empty={empty}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
          <defs>
            <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDay} tick={axis} minTickGap={24} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={axis} axisLine={false} tickLine={false} width={34} />
          <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} />
          {showResellers && <Legend wrapperStyle={{ fontSize: 12 }} />}
          <Area type="monotone" dataKey="customers" name="Customers" stroke="#8b5cf6" strokeWidth={2} fill="url(#gc)" />
          {showResellers && <Area type="monotone" dataKey="resellers" name="Resellers" stroke="#10b981" strokeWidth={2} fill="url(#gr)" />}
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function CallsChart({ data }) {
  const empty = !data || data.every((d) => (d.calls || 0) === 0);
  return (
    <ChartCard testid="chart-calls" title="Calls over time" subtitle="Last 14 days" empty={empty}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
          <defs>
            <linearGradient id="gcall" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDay} tick={axis} minTickGap={24} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={axis} axisLine={false} tickLine={false} width={34} />
          <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} />
          <Area type="monotone" dataKey="calls" name="Calls" stroke="#0ea5e9" strokeWidth={2} fill="url(#gcall)" />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function TopCustomersChart({ data }) {
  const empty = !data || data.length === 0;
  return (
    <ChartCard testid="chart-top-customers" title="Top customers by calls" subtitle="Today" empty={empty}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={axis} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" tick={axis} width={110} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
          <Bar dataKey="calls" name="Calls" radius={[0, 6, 6, 0]}>
            {data?.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function DonutChart({ title, subtitle, testid, data }) {
  const rows = (data || []).map((d) => ({ name: prettyLabel(d.name), value: d.value }));
  const empty = rows.length === 0 || rows.every((d) => d.value === 0);
  return (
    <ChartCard testid={testid} title={title} subtitle={subtitle} empty={empty}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
            {rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [dash, setDash] = useState(null);

  const platform = isPlatformOperator(user);

  useEffect(() => {
    if (user?.role === "staff" && !can(user, "dashboard", "view")) return;
    api.get("/stats/summary").then((r) => setStats(r.data)).catch(() => {});
    api.get("/stats/dashboard").then((r) => setDash(r.data)).catch(() => {});
    if (!platform)
      api.get("/wallets/me").then((r) => setWallet(r.data)).catch(() => {});
  }, [user, platform]);

  if (user?.role === "staff" && !can(user, "dashboard", "view")) {
    return <Navigate to={firstAllowedPath(user)} replace />;
  }

  const greeting = {
    admin: "Platform overview",
    staff: "Platform overview",
    reseller: "Reseller overview",
    customer: "Account overview",
  }[user?.role];
  const role = user?.role;

  return (
    <div>
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle={`${greeting} · strict tenant-isolated data`} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {platform && <>
          <LiveCallsTile />
          <StatCard testid="stat-resellers" icon={Storefront} label="Resellers" value={stats?.resellers ?? "—"} tone="primary" />
          <StatCard testid="stat-customers" icon={UsersThree} label="Customers" value={stats?.customers ?? "—"}
            hint={`${stats?.direct_customers ?? 0} direct · ${stats?.reseller_customers ?? 0} via resellers`} tone="success" />
          <StatCard testid="stat-opensips" icon={ShareNetwork} label="OpenSIPS Servers" value={stats?.opensips_servers ?? "—"} tone="warning" />
          <StatCard testid="stat-media" icon={WaveSine} label="Media Servers" value={stats?.media_servers ?? "—"} tone="muted" />
          <StatCard testid="stat-campaigns" icon={Megaphone} label="Global Campaigns" value={stats?.campaigns ?? "—"} tone="primary" />
          <StatCard testid="stat-reservations" icon={StackSimple} label="Active Holds" value={stats?.active_reservations ?? "—"} tone="warning" />
          <StatCard testid="stat-direct" icon={UserCircle} label="Direct Customers" value={stats?.direct_customers ?? "—"} tone="success" />
        </>}

        {role === "reseller" && <>
          <LiveCallsTile />
          <StatCard testid="stat-customers" icon={UsersThree} label="My Customers" value={stats?.customers ?? "—"} tone="success" />
          <StatCard testid="stat-campaigns" icon={Megaphone} label="Campaigns" value={stats?.campaigns ?? "—"} tone="warning" />
          {wallet && <StatCard testid="stat-wallet" icon={Wallet} label="Available Balance"
            value={money(wallet.available_minor)} tone="success" />}
        </>}

        {role === "customer" && <>
          <LiveCallsTile />
          <StatCard testid="stat-campaigns" icon={Megaphone} label="My Campaigns" value={stats?.campaigns ?? "—"} tone="warning" />
          <StatCard testid="stat-reservations" icon={StackSimple} label="Active Holds" value={stats?.active_reservations ?? "—"} tone="primary" />
          {wallet && <StatCard testid="stat-wallet" icon={Wallet} label="Available Balance"
            value={money(wallet.available_minor)} tone="success" />}
        </>}
      </div>

      {/* Detailed analytics */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dashboard-charts">
        {platform && <>
          <SignupsChart data={dash?.signups_series} showResellers />
          <CallsChart data={dash?.calls_series} />
          <TopCustomersChart data={dash?.top_customers_today} />
          <DonutChart testid="chart-transfer-outcomes" title="Transfer outcomes" subtitle="Today"
            data={dash?.transfer_outcomes_today} />
        </>}

        {role === "reseller" && <>
          <SignupsChart data={dash?.signups_series} showResellers={false} />
          <CallsChart data={dash?.calls_series} />
          <TopCustomersChart data={dash?.top_customers_today} />
        </>}

        {role === "customer" && <>
          <CallsChart data={dash?.calls_series} />
          <DonutChart testid="chart-transfer-outcomes" title="Transfer outcomes" subtitle="Today"
            data={dash?.transfer_outcomes_today} />
          <DonutChart testid="chart-dispositions" title="Call dispositions" subtitle="Today"
            data={dash?.dispositions_today} />
        </>}
      </div>
    </div>
  );
}
